import { usePersistedState } from "@/lib/persistedFilters";
// Iter 306 — Accounting queue: accepted pro formas waiting for their QuickBooks entry.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, BookCheck, CheckCircle2, Download, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { money } from "@/components/commissions/engineShared";

const TABS = [["pending", "To enter"], ["entered", "Entered"], ["void", "Void"]];

export default function AccountingQueue() {
  const navigate = useNavigate();
  const [status, setStatus] = usePersistedState("accounting-queue.status", "pending");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [entering, setEntering] = useState(null);
  const load = () => api.get("/commissions/accounting/queue", { params: { status } })
    .then(({ data: d }) => { setData(d); setError(""); })
    .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not load the queue"));
  useEffect(() => { load(); }, [status]);
  const reopen = async (it) => {
    try { await api.post(`/commissions/accounting/queue/${it.queue_id}/reopen`); toast.success("Back in the queue"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="mx-auto max-w-[1100px] px-4 py-6" data-testid="accounting-queue-page">
      <Button variant="ghost" size="sm" onClick={() => navigate("/commissions")} className="h-8 -ml-2 mb-3" data-testid="accounting-back">
        <ArrowLeft className="h-4 w-4" /> Back to Commissions
      </Button>
      <PageHeader testid="accounting-header" title="Accounting queue" icon={BookCheck}
        subtitle="Every accepted pro forma lands here until it is entered in QuickBooks. Voided entries were superseded by a corrected pro forma or a revoked acceptance." />
      {error && <Card className="border-rose-200 bg-rose-50 p-4 text-sm text-rose-700" data-testid="accounting-error">{error}</Card>}
      {data && (
        <>
          <div className="mb-3 flex items-center gap-1">
            {TABS.map(([k, label]) => (
              <button key={k} type="button" onClick={() => setStatus(k)} data-testid={`accounting-tab-${k}`}
                      className={`rounded-full px-3 py-1 text-xs font-medium ${status === k ? "bg-primary text-primary-foreground" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                {label} <span className="opacity-70">{data.counts?.[k] ?? 0}</span>
              </button>
            ))}
            <Button variant="outline" size="sm" className="ml-auto h-8" disabled={!data.items?.length}
                    onClick={() => window.open(`${api.defaults.baseURL}/commissions/accounting/queue/export.csv?status=${status}`, "_blank")}
                    data-testid="accounting-export-csv" title="One row per pro forma — entries stay in this tab until marked entered">
              <Download className="h-4 w-4" /> Download CSV for QuickBooks
            </Button>
          </div>
          <Card className="border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Partner</th><th className="px-3 py-2 text-left">Period</th>
                  <th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2 text-left">Accepted by</th>
                  <th className="px-3 py-2 text-left">{status === "pending" ? "Queued" : status === "entered" ? "Entered" : "Voided"}</th>
                  <th className="px-3 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.items.length === 0 && <tr><td colSpan={6} className="px-3 py-10 text-center text-slate-400" data-testid="accounting-empty">Nothing here.</td></tr>}
                {data.items.map((it) => (
                  <tr key={it.queue_id} className="hover:bg-slate-50/60" data-testid={`accounting-row-${it.queue_id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-800">{it.partner_name}{it.version > 1 && <span className="ml-1.5 rounded bg-violet-100 px-1 text-[10px] text-violet-700">v{it.version}</span>}</div>
                      {it.qb_reference && <div className="text-xs text-slate-500">QB ref {it.qb_reference}</div>}
                      {it.void_reason && <div className="text-xs text-rose-600">{it.void_reason}</div>}
                    </td>
                    <td className="px-3 py-2 text-slate-600">{it.period_label}</td>
                    <td className="px-3 py-2 text-right font-medium">{money(it.amount)}<div className="text-[10px] text-slate-400">{it.deals_count} deals · tax {money(it.tax)}</div></td>
                    <td className="px-3 py-2 text-xs text-slate-600">{it.accepted_by_name}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{(status === "pending" ? it.created_at : status === "entered" ? it.entered_at : it.voided_at)?.slice(0, 16).replace("T", " ")}{status === "entered" && <div>{it.entered_by_name}</div>}</td>
                    <td className="px-3 py-2 text-right">
                      {status === "pending" && data.can_enter && <Button size="sm" className="h-7 text-xs" onClick={() => setEntering(it)} data-testid={`accounting-enter-${it.queue_id}`}><CheckCircle2 className="h-3.5 w-3.5" /> Entered in QuickBooks</Button>}
                      {status === "entered" && data.can_enter && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => reopen(it)} data-testid={`accounting-reopen-${it.queue_id}`}><RotateCcw className="h-3.5 w-3.5" /> Reopen</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
      {entering && <EnteredDialog item={entering} onClose={() => setEntering(null)} onDone={load} />}
    </div>
  );
}

function EnteredDialog({ item, onClose, onDone }) {
  const [ref, setRef] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/commissions/accounting/queue/${item.queue_id}/entered`, { qb_reference: ref, note });
      toast.success("Marked as entered"); onDone(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="accounting-entered-dialog">
        <DialogHeader>
          <DialogTitle>Entered in QuickBooks</DialogTitle>
          <DialogDescription>{item.partner_name} · {item.period_label} · {money(item.amount)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">QuickBooks reference (bill / invoice #)</Label><Input value={ref} onChange={(e) => setRef(e.target.value)} data-testid="accounting-qb-ref" /></div>
          <div><Label className="text-xs">Note (optional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="accounting-note" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="accounting-entered-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mark entered"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
